// Stores
import { appStoreActions } from '@/stores';

// Providers
import { useFirebase } from '@/providers/firebase';

// Utils
import { getUserRole, hasCodeProperty, hasMembersProperty } from '@/utils';

// Constants
import { UserRole } from '@shared/constants';
import { AUTH_ERROR_MESSAGES } from '@/constants';

// Types
import { ActivityType, ProjectInfo } from '@shared/types';
import { MemberAddPayloadSocket, UserProfile } from '@/types';

import { useWebSocketMessage } from './useWebSocket';

export const useGlobalActivityHandler = () => {
  const { user } = useFirebase();

  useWebSocketMessage(async (message) => {
    if (!message?.channel || !message?.payload) return;
    const userRole = getUserRole();
    const isUser = userRole === UserRole.USER;
    const {
      updateUser,
      setArchiveProject,
      addProject,
      removeProject,
      setForceLogoutWithMessage,
    } = appStoreActions;

    switch (message.channel) {
      case ActivityType.USER_ACTIVATE:
      case ActivityType.USER_DEACTIVATE:
        updateUser?.(message.payload as UserProfile);
        break;
      case ActivityType.PROJECT_ARCHIVE:
      case ActivityType.PROJECT_UNARCHIVE:
        if (hasCodeProperty(message.payload)) {
          const projectPayload = message.payload as ProjectInfo;
          setArchiveProject?.(
            projectPayload.code || '',
            projectPayload.archived || false,
          );
        }
        break;
      case ActivityType.PROJECT_CREATE:
        if (hasCodeProperty(message.payload)) {
          const projectPayload = message.payload as ProjectInfo;
          if (userRole === UserRole.ADMIN) {
            addProject?.({ projectInfo: projectPayload });
          }
        }
        break;
      case ActivityType.PROJECT_MEMBER_REMOVE:
      case ActivityType.PROJECT_MEMBER_LEAVE:
        if (hasMembersProperty(message.payload)) {
          const { id, members } = message.payload as MemberAddPayloadSocket;
          if (isUser && members.some((member) => member.user === user?.email)) {
            removeProject?.([id]);
          }
        }
        break;
      case ActivityType.PROJECT_MEMBER_JOIN:
      case ActivityType.PROJECT_MEMBER_RESUME:
        if (hasMembersProperty(message.payload)) {
          const { members, ...rest } =
            message.payload as MemberAddPayloadSocket;
          const isCurrentUser = members.find(
            (item) => item.user === user?.email,
          );
          if (isCurrentUser && isUser) {
            addProject?.({ projectInfo: { ...rest } });
          }
        }
        break;

      case ActivityType.USER_UPDATE_ROLE: {
        if (typeof message.payload === 'object' && 'role' in message.payload) {
          const userInfoUpdate = message.payload as UserProfile;
          const userRoleUpdate = userInfoUpdate.role || '';

          if (userInfoUpdate.email !== user?.email) return;

          if (userRoleUpdate && userRoleUpdate !== userRole) {
            setForceLogoutWithMessage(AUTH_ERROR_MESSAGES.ROLE_MISMATCH);
          }
        }
      }
    }
  });
};
